"use client";

import { Theme } from "@itzme1on/uikit";
import { setCookie } from "cookies-next";
import { createContext, useContext, useState } from "react";
import { COOKIE_THEME_KEY } from "../../../constants/constants";

export type ThemeName = "light" | "dark";

export interface ThemeItem {
	name: ThemeName;
	theme: Theme;
}

interface ThemeContextValue {
	theme: Theme;
	themeName: ThemeName;
	setTheme: (name: ThemeName) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
	const value = useContext(ThemeContext);

	if (!value) {
		throw new Error("useTheme must be used within ThemeProvider");
	}

	return value;
};

interface ThemeProviderProps {
	initialThemeName: ThemeName;
	themes: ThemeItem[];
	children: React.ReactNode;
}

export const ThemeProvider = ({
	children,
	initialThemeName,
	themes,
}: ThemeProviderProps) => {
	const [themeName, setThemeName] = useState<ThemeName>(initialThemeName);

	const activeItem =
		themes.find((item) => item.name === themeName) ?? themes[0];

	const setTheme = (name: ThemeName) => {
		const item = themes.find((t) => t.name === name);
		if (!item) return;

		setThemeName(name);
		setCookie(COOKIE_THEME_KEY, name, {
			maxAge: 60 * 60 * 24 * 365,
			sameSite: "lax",
		});

		document.documentElement.dataset.theme = name;
		document
			.querySelector('meta[name="theme-color"]')
			?.setAttribute("content", item.theme.backgroundBody ?? "#ffffff");
	};

	const toggleTheme = () => setTheme(themeName === "dark" ? "light" : "dark");

	return (
		<ThemeContext.Provider
			value={{ theme: activeItem.theme, themeName, setTheme, toggleTheme }}
		>
			{children}
		</ThemeContext.Provider>
	);
};
